<template>
  <div ref="rootElement" style="opacity: 0">
    <!-- Hidden table for search functionality -->
    <table data-search-table class="sr-only">
      <thead>
        <tr>
          <th>Ref</th>
          <th>Name</th>
          <th>Location</th>
          <th>Year</th>
          <th>Status</th>
          <th>Budget</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(project, index) in sortedProjects" :key="`table-${project.id}-${index}`">
          <td>
            <a :href="project.url">{{ project.referenceDisplay }}</a>
          </td>
          <td>
            <a :href="project.url">{{ project.title }}</a>
          </td>
          <td>
            <a :href="project.url">{{ project.locationDisplay }}</a>
          </td>
          <td>
            <a :href="project.url">{{ project.yearDisplay }}</a>
          </td>
          <td>
            <a :href="project.url">{{ project.status }}</a>
          </td>
          <td>
            <a :href="project.url">{{ project.budgetDisplay }}</a>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Visual display -->
    <div data-search-visual aria-hidden="true" class="-mt-9 w-full text-white">
      <div class="sticky z-[100] left-0 w-full top-header border-0 border-t border-white border-solid">
        <div class="flex font-light uppercase bg-black max-lg:text-sm style-20">
          <button
            @click="sortBy('ref')"
            :class="[
              refColClass,
              'p-2 font-light text-left border-0 border-l border-r border-b border-white border-solid'
            ]"
          >
            <span class="uppercase">Ref</span>
            <img
              width="24"
              height="15"
              :src="sortIconUrl"
              alt="Sort"
              class="ml-1 lg:ml-[6px] h-[12px] lg:h-[16px] w-fit translate-y-[-1px]"
            />
          </button>
          <button
            @click="sortBy('name')"
            :class="[
              nameColClass,
              'p-2 font-light text-left border-0 border-r border-b border-white border-solid'
            ]"
          >
            <span class="uppercase">Name</span>
            <img
              width="24"
              height="15"
              :src="sortIconUrl"
              alt="Sort"
              class="ml-1 lg:ml-[6px] h-[12px] lg:h-[16px] w-fit translate-y-[-1px]"
            />
          </button>
          <button
            @click="sortBy('location')"
            :class="[
              locationColClass,
              'p-2 font-light text-left border-0 border-r border-b border-white border-solid lax max-lg:hidden'
            ]"
          >
            <span class="uppercase">Location</span>
            <img
              width="24"
              height="15"
              :src="sortIconUrl"
              alt="Sort"
              class="ml-1 lg:ml-[6px] h-[12px] lg:h-[16px] w-fit translate-y-[-1px]"
            />
          </button>
          <button
            @click="sortBy('year')"
            :class="[
              yearColClass,
              'p-2 font-light text-left border-0 border-r border-b border-white border-solid max-lg:hidden'
            ]"
          >
            <span class="uppercase">Year</span>
            <img
              width="24"
              height="15"
              :src="sortIconUrl"
              alt="Sort"
              class="ml-1 lg:ml-[6px] h-[12px] lg:h-[16px] w-fit translate-y-[-1px]"
            />
          </button>
          <button
            @click="sortBy('status')"
            :class="[
              statusColClass,
              'p-2 font-light text-left border-0 border-r border-b border-white border-solid max-lg:hidden'
            ]"
          >
            <span class="uppercase">Status</span>
            <img
              width="24"
              height="15"
              :src="sortIconUrl"
              alt="Sort"
              class="ml-1 lg:ml-[6px] h-[12px] lg:h-[16px] w-fit translate-y-[-1px]"
            />
          </button>
          <button
            @click="sortBy('budget')"
            :class="[
              budgetColClass,
              'p-2 font-light text-left border-0 border-r border-b border-white border-solid max-lg:hidden'
            ]"
          >
            <span class="uppercase">Budget</span>
            <img
              width="24"
              height="15"
              :src="sortIconUrl"
              alt="Sort"
              class="ml-1 lg:ml-[6px] h-[12px] lg:h-[16px] w-fit translate-y-[-1px]"
            />
          </button>
        </div>
      </div>

      <div class="pr-0 uppercase border-0 border-white border-solid max-lg:border-r">
        <a
          v-for="(project, index) in sortedProjects"
          :key="`visual-${project.id}-${index}`"
          :href="project.url"
          class="flex overflow-clip"
        >
          <div :class="[refColClass, 'border-0 border-r border-l border-b border-white border-solid']">
            <span :class="[refColClass, 'block p-2 truncate max-lg:text-sm style-20 3xl:w-full']">
              {{ project.referenceDisplay }}
            </span>
          </div>
          <div :class="[nameColClass, 'relative border-0 lg:border-r border-b border-white border-solid']">
            <span class="block absolute inset-0 p-2 truncate max-lg:text-sm style-20">
              {{ project.title }}
            </span>
          </div>
          <div :class="[locationColClass, 'border-0 border-r border-b border-white border-solid max-lg:hidden']">
            <span :class="[locationColClass, 'block p-2 truncate max-lg:text-sm style-20 3xl:w-full']">
              {{ project.locationDisplay }}
            </span>
          </div>
          <div :class="[yearColClass, 'border-0 border-r border-b border-white border-solid max-lg:hidden']">
            <span :class="[yearColClass, 'block p-2 truncate max-lg:text-sm style-20 3xl:w-full']">
              {{ project.yearDisplay }}
            </span>
          </div>
          <div :class="[statusColClass, 'border-0 border-r border-b border-white border-solid max-lg:hidden']">
            <span :class="[statusColClass, 'block p-2 truncate max-lg:text-sm style-20 3xl:w-full']">
              {{ project.status }}
            </span>
          </div>
          <div :class="[budgetColClass, 'border-0 border-r border-b border-white border-solid max-lg:hidden']">
            <span :class="[budgetColClass, 'block p-2 truncate max-lg:text-sm style-20 3xl:w-full']">
              {{ project.budgetDisplay }}
            </span>
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import gsap from 'gsap';

export default {
  name: 'ProjectList',
  props: {
    projects: {
      type: Array,
      required: true
    },
    sortIconUrl: {
      type: String,
      required: true
    },
    refColClass: {
      type: String,
      default: 'w-[75px] lg:w-[120px] 3xl:w-[6%]'
    },
    nameColClass: {
      type: String,
      default: 'flex-1 3xl:flex-none 3xl:w-[54%]'
    },
    locationColClass: {
      type: String,
      default: 'w-[220px] 3xl:w-[12%]'
    },
    yearColClass: {
      type: String,
      default: 'w-[130px] 3xl:w-[7%]'
    },
    statusColClass: {
      type: String,
      default: 'w-[225px] 3xl:w-[12%]'
    },
    budgetColClass: {
      type: String,
      default: 'w-[165px] 3xl:w-[9%]'
    }
  },
  data() {
    return {
      sortField: null,
      sortDirection: 'asc', // 'asc' or 'desc'
      searchQuery: '' // Search filter
    }
  },
  mounted() {
    // Check for search query in URL on mount
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('search');
    if (searchQuery) {
      this.searchQuery = searchQuery;
    }

    console.log(this.refColClass);
    // Listen for search events from SearchNavigation
    window.addEventListener('projectListSearch', this.handleSearchEvent);

    // Animate fade-in after component is fully rendered
    this.$nextTick(() => {
      gsap.to(this.$refs.rootElement, {
        opacity: 1,
        duration: 0.6,
        ease: 'power2.out'
      });
    });
  },
  beforeUnmount() {
    // Clean up event listener
    window.removeEventListener('projectListSearch', this.handleSearchEvent);
  },
  computed: {
    filteredProjects() {
      if (!this.searchQuery) {
        return this.projects;
      }

      const query = this.searchQuery.toLowerCase();
      return this.projects.filter(project => {
        // Search across multiple fields
        const searchableText = [
          project.referenceDisplay,
          project.title,
          project.locationDisplay,
          project.yearDisplay,
          project.status,
          project.budgetDisplay
        ].join(' ').toLowerCase();

        return searchableText.includes(query);
      });
    },
    sortedProjects() {
      const projectsToSort = this.filteredProjects;
      
      if (!this.sortField) {
        return projectsToSort;
      }

      const sorted = [...projectsToSort].sort((a, b) => {
        let aValue, bValue

        switch (this.sortField) {
          case 'ref':
            // Numerical sort by referenceNumber
            aValue = a.referenceNumber ?? 0
            bValue = b.referenceNumber ?? 0
            return this.sortDirection === 'asc' ? aValue - bValue : bValue - aValue

          case 'name':
            // Alphabetical sort by title
            aValue = (a.title || '').toLowerCase()
            bValue = (b.title || '').toLowerCase()
            if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1
            if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1
            return 0

          case 'location':
            // Alphabetical sort by locationDisplay
            aValue = (a.locationDisplay || '').toLowerCase()
            bValue = (b.locationDisplay || '').toLowerCase()
            if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1
            if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1
            return 0

          case 'year':
            // Numerical sort by yearStart
            aValue = a.yearStart ?? 0
            bValue = b.yearStart ?? 0
            return this.sortDirection === 'asc' ? aValue - bValue : bValue - aValue

          case 'status':
            // Alphabetical sort by status
            aValue = (a.status || '').toLowerCase()
            bValue = (b.status || '').toLowerCase()
            if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1
            if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1
            return 0

          case 'budget':
            // Numerical sort by budgetNumber
            // Handle undisclosed - put at end
            if (a.undisclosed && !b.undisclosed) return this.sortDirection === 'asc' ? 1 : -1
            if (!a.undisclosed && b.undisclosed) return this.sortDirection === 'asc' ? -1 : 1
            if (a.undisclosed && b.undisclosed) return 0

            aValue = a.budgetNumber ?? 0
            bValue = b.budgetNumber ?? 0
            return this.sortDirection === 'asc' ? aValue - bValue : bValue - aValue

          default:
            return 0
        }
      })

      return sorted
    }
  },
  methods: {
    sortBy(field) {
      if (this.sortField === field) {
        // Toggle direction if clicking the same field
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        // New field, start with ascending
        this.sortField = field
        this.sortDirection = 'asc'
      }
    },
    handleSearchEvent(event) {
      this.searchQuery = event.detail.searchValue;
    }
  }
}
</script>

